import React, { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Description as DocIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { tokens } from "@/tokens";

// ─── Custom Requests (no slotId) shown in the declarations section ───

interface CustomRequestBlockProps {
  existingRequests: any[];
  sectionId: string;
  onRevisionUploadClick: (requestId: string, category: string, slotId: string, reason: string) => void;
  onEditRequest: (request: any) => void;
  onDeleteRequest: (requestId: string) => void;
}

export const CustomRequestBlock: React.FC<CustomRequestBlockProps> = ({
  existingRequests,
  sectionId,
  onRevisionUploadClick,
  onEditRequest,
  onDeleteRequest,
}) => {
  const customRequests = existingRequests.filter((r: any) => !r.slotId && r.status === "pending");

  if (customRequests.length === 0 || sectionId !== "declarations") return null;

  return (
    <>
      {customRequests.map((req: any) => (
        <Box
          key={req._id}
          sx={{
            mx: 2,
            mb: 1,
            border: `1px dashed ${tokens.status.warning}50`,
            borderRadius: tokens.radius.sm,
            bgcolor: `${tokens.status.warning}04`,
            px: 2,
            py: 1.25,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <DocIcon sx={{ fontSize: 16, color: tokens.status.warning }} />
              <Typography sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.semibold, color: tokens.text.primary }}>
                {req.label}
              </Typography>
              <Chip label="Custom Request" size="small" sx={{ height: 18, fontSize: 9, bgcolor: `${tokens.status.warning}15`, color: tokens.status.warning }} />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Tooltip title="Edit request">
                <IconButton
                  size="small"
                  onClick={() => onEditRequest(req)}
                  sx={{
                    color: tokens.text.muted,
                    p: 0.5,
                    "&:hover": { color: tokens.brand.primary, bgcolor: `${tokens.brand.primary}08` },
                  }}
                >
                  <EditIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete request">
                <IconButton
                  size="small"
                  onClick={() => onDeleteRequest(req._id)}
                  sx={{
                    color: tokens.text.muted,
                    p: 0.5,
                    "&:hover": { color: tokens.status.danger, bgcolor: `${tokens.status.danger}08` },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
              <Button
                size="small"
                variant="contained"
                startIcon={<UploadIcon sx={{ fontSize: 14 }} />}
                onClick={() => onRevisionUploadClick(req._id, "ADDITIONAL", "additional", req.reason)}
                sx={{
                  textTransform: "none",
                  fontSize: tokens.fontSize["2xs"],
                  fontWeight: tokens.fontWeight.semibold,
                  bgcolor: tokens.status.warning,
                  borderRadius: tokens.radius.sm,
                  boxShadow: "none",
                  px: 1.5,
                  py: 0.25,
                  minHeight: 26,
                  "&:hover": { bgcolor: "#b45309", boxShadow: "none" },
                }}
              >
                Upload
              </Button>
            </Stack>
          </Stack>
          <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.body, ml: 3.5 }}>
            {req.reason}
          </Typography>
          {req.requirements?.length > 0 && (
            <Box sx={{ ml: 3.5, mt: 0.25 }}>
              {req.requirements.map((r: string, i: number) => (
                <Typography key={i} sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted }}>
                  {"\u2022"} {r}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      ))}
    </>
  );
};

// ─── Validation issue label helper ──────────────────────────────────

const formatItemLabel = (itemId: string): string =>
  itemId
    .replace(/^(po[12]|inv[12]|pay[12]|cbs|ast|bol)_/, "")
    .replace(/_/g, " ")
    .replace(/^\w/, (c: string) => c.toUpperCase());

// ─── Request Revision Dialog ─────────────────────────────────────────

export interface ValidationIssue {
  itemId: string;
  status: string;
  detail?: string;
}

interface RequestRevisionDialogProps {
  requestTarget: { slotId: string; label: string } | null;
  requestReason: string;
  requestReqs: string;
  validationIssues?: ValidationIssue[];
  onReasonChange: (value: string) => void;
  onReqsChange: (value: string) => void;
  onClose: () => void;
  onSubmit: (selectedIssues?: ValidationIssue[]) => void;
}

export const RequestRevisionDialog: React.FC<RequestRevisionDialogProps> = ({
  requestTarget,
  requestReason,
  requestReqs,
  validationIssues,
  onReasonChange,
  onReqsChange,
  onClose,
  onSubmit,
}) => {
  const [selectedIssueIds, setSelectedIssueIds] = useState<Set<string>>(new Set());

  const toggleIssue = (issue: ValidationIssue) => {
    setSelectedIssueIds((prev) => {
      const next = new Set(prev);
      if (next.has(issue.itemId)) {
        next.delete(issue.itemId);
      } else {
        next.add(issue.itemId);
      }
      return next;
    });
  };

  const handleClose = () => {
    setSelectedIssueIds(new Set());
    onClose();
  };

  const handleSubmit = () => {
    const selected = validationIssues?.filter((i) => selectedIssueIds.has(i.itemId));
    onSubmit(selected && selected.length > 0 ? selected : undefined);
    setSelectedIssueIds(new Set());
  };

  const hasSelectedIssues = selectedIssueIds.size > 0;
  const canSubmit = requestReason.trim() || hasSelectedIssues;

  return (
    <Dialog
      open={!!requestTarget}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: tokens.radius.lg } }}
    >
      <DialogTitle sx={{ fontSize: tokens.fontSize.lg, fontWeight: tokens.fontWeight.heavy, color: tokens.text.primary, pb: 0.5 }}>
        Request Document Revision
      </DialogTitle>
      <DialogContent>
        <Box sx={{ bgcolor: tokens.bg.muted, borderRadius: tokens.radius.md, px: 1.5, py: 1, mb: 2, mt: 1 }}>
          <Typography sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.semibold, color: tokens.text.primary }}>
            {requestTarget?.label}
          </Typography>
        </Box>

        {/* Validation Issues Picker */}
        {validationIssues && validationIssues.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.semibold, color: tokens.text.primary, mb: 0.75 }}>
              Link validation issues
            </Typography>
            <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, mb: 1 }}>
              Select issues found by AI validation to include with this request.
            </Typography>
            <Stack spacing={0.5}>
              {validationIssues.map((issue) => {
                const isSelected = selectedIssueIds.has(issue.itemId);
                const isFail = issue.status === "FAIL";
                const statusColor = isFail ? tokens.status.danger : tokens.status.warning;

                return (
                  <Stack
                    key={issue.itemId}
                    direction="row"
                    alignItems="flex-start"
                    spacing={0.5}
                    onClick={() => toggleIssue(issue)}
                    sx={{
                      px: 1,
                      py: 0.75,
                      borderRadius: tokens.radius.sm,
                      border: `1px solid ${isSelected ? statusColor : tokens.border.light}`,
                      bgcolor: isSelected ? `${statusColor}06` : tokens.bg.surface,
                      cursor: "pointer",
                      transition: "all 150ms ease",
                      "&:hover": { borderColor: statusColor, bgcolor: `${statusColor}04` },
                    }}
                  >
                    <Checkbox
                      checked={isSelected}
                      size="small"
                      sx={{
                        p: 0.25,
                        color: tokens.border.default,
                        "&.Mui-checked": { color: statusColor },
                      }}
                      tabIndex={-1}
                    />
                    <Box sx={{ flex: 1, minWidth: 0, pt: 0.1 }}>
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Chip
                          label={isFail ? "Failed" : "Warning"}
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: 9,
                            fontWeight: tokens.fontWeight.semibold,
                            bgcolor: `${statusColor}12`,
                            color: statusColor,
                          }}
                        />
                        <Typography sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.semibold, color: tokens.text.primary }}>
                          {formatItemLabel(issue.itemId)}
                        </Typography>
                      </Stack>
                      {issue.detail && (
                        <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, mt: 0.25, lineHeight: 1.4 }}>
                          {issue.detail}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        )}

        <TextField
          label={hasSelectedIssues ? "Additional notes (optional)" : "Reason for revision *"}
          value={requestReason}
          onChange={(e) => onReasonChange(e.target.value)}
          fullWidth
          size="small"
          multiline
          minRows={2}
          placeholder="e.g., Document is missing a signature, wrong party name listed, values don't match PO..."
          sx={{ mb: 2, "& .MuiOutlinedInput-root": { fontSize: tokens.fontSize.sm2, borderRadius: tokens.radius.md } }}
        />
        <TextField
          label="Specific requirements (optional, one per line)"
          value={requestReqs}
          onChange={(e) => onReqsChange(e.target.value)}
          fullWidth
          size="small"
          multiline
          minRows={2}
          placeholder={"Must include authorized signature\nDated within the last 90 days\nMust show correct buyer name"}
          sx={{ "& .MuiOutlinedInput-root": { fontSize: tokens.fontSize.sm2, borderRadius: tokens.radius.md } }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={handleClose}
          sx={{ textTransform: "none", fontSize: tokens.fontSize.sm2, color: tokens.text.secondary }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={handleSubmit}
          sx={{
            textTransform: "none",
            fontSize: tokens.fontSize.sm2,
            fontWeight: tokens.fontWeight.semibold,
            bgcolor: tokens.brand.primary,
            borderRadius: tokens.radius.md,
            boxShadow: "none",
            px: 3,
            "&:hover": { bgcolor: tokens.brand.primaryHover, boxShadow: "none" },
          }}
        >
          Add to Requests
          {hasSelectedIssues && (
            <Chip
              label={selectedIssueIds.size}
              size="small"
              sx={{ ml: 1, height: 18, fontSize: 10, bgcolor: "rgba(255,255,255,0.25)", color: "inherit" }}
            />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
